import { warehouseClient } from "@/infrastructure/http/warehouseClient";
import type {
  ErpDocumentListResponse,
  ErpValidationResponse,
  ErpExecutionResponse,
} from "@/domain/types";

export async function listErpDocuments(params: {
  status?: string;
  type?: string;
  page?: number;
  limit?: number;
  search?: string;
}): Promise<ErpDocumentListResponse> {
  const query = new URLSearchParams();
  if (params.status) query.set("status", params.status);
  if (params.type) query.set("type", params.type);
  if (params.page) query.set("page", String(params.page));
  if (params.limit) query.set("limit", String(params.limit));
  if (params.search) query.set("search", params.search);
  const response = await warehouseClient.get<any>(
    `/erp-documents?${query.toString()}`
  );
  // Handle multiple response shapes:
  // Shape 1: { data: { data: [...], total, page, limit } } (ApiEnvelope)
  // Shape 2: { data: [...], total, page, limit }
  // Shape 3: { data: [...] } (array directly in data)
  const raw = response.data?.data ?? response.data;
  if (Array.isArray(raw)) {
    return { data: raw, total: raw.length, page: params.page ?? 1, limit: params.limit ?? 25 };
  }
  if (raw && Array.isArray(raw.items)) {
    return { data: raw.items, total: raw.meta?.totalItems ?? raw.items.length, page: raw.meta?.currentPage ?? params.page ?? 1, limit: raw.meta?.itemsPerPage ?? params.limit ?? 25 };
  }
  if (raw && Array.isArray(raw.data)) {
    return { data: raw.data, total: raw.total ?? raw.data.length, page: raw.page ?? params.page ?? 1, limit: raw.limit ?? params.limit ?? 25 };
  }
  return { data: [], total: 0, page: 1, limit: 25 };
}

export async function validateErpDocuments(documentIds: string[]): Promise<ErpValidationResponse> {
  const response = await warehouseClient.post<ApiEnvelope<ErpValidationResponse>>(
    "/erp-documents/validate",
    { documentIds }
  );
  return response.data.data;
}

export async function executeErpDocuments(params: {
  documentIds: string[];
  dispatchId?: string;
  receiptId?: string;
  adjustmentId?: string;
}): Promise<ErpExecutionResponse> {
  const response = await warehouseClient.post<ApiEnvelope<ErpExecutionResponse>>(
    "/erp-documents/execute",
    params
  );
  return response.data.data;
}

export async function rejectErpDocument(id: string): Promise<void> {
  await warehouseClient.patch(`/erp-documents/${id}/status`, { status: "REJECTED" });
}

export async function assignErpDocumentWarehouse(id: string, warehouseId: string): Promise<void> {
  await warehouseClient.patch(`/erp-documents/${id}/warehouse`, { warehouseId });
}

export async function countPendingErpDocuments(type: string): Promise<number> {
  try {
    const query = new URLSearchParams();
    query.set("status", "PENDING");
    query.set("type", type);
    query.set("page", "1");
    query.set("limit", "1");
    const response = await warehouseClient.get<any>(`/erp-documents?${query.toString()}`);
    const raw = response.data?.data ?? response.data;
    if (Array.isArray(raw)) return raw.length;
    if (raw && Array.isArray(raw.data)) return raw.data.length;
    return 0;
  } catch {
    return 0;
  }
}
